// File: frontend/src/pages/AuditLogPage.jsx
// Version: 1.0.0 (2026-04-11)
// Description: Admin audit log viewer with filters and CSV/JSON export.

import React, { useState, useEffect, useRef } from 'react';

const API_BASE_URL = '/api/v1';

const AuditLogPage = () => {
    const [filters, setFilters] = useState({
        action: '',
        resource_type: '',
        start_date: '',
        end_date: '',
    });
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [offset, setOffset] = useState(0);
    const limit = 50;

    const debounceRef = useRef(null);

    const buildQueryString = (extraParams = {}) => {
        const params = new URLSearchParams();
        if (filters.action) params.set('action', filters.action);
        if (filters.resource_type) params.set('resource_type', filters.resource_type);
        if (filters.start_date) params.set('start_date', filters.start_date + 'T00:00:00');
        if (filters.end_date) params.set('end_date', filters.end_date + 'T23:59:59');
        Object.entries(extraParams).forEach(([k, v]) => params.set(k, v));
        return params.toString();
    };

    const fetchLogs = async (currentOffset = 0) => {
        setLoading(true);
        setError(null);
        try {
            const qs = buildQueryString({ limit, offset: currentOffset });
            const url = `${API_BASE_URL}/admin/audit-logs${qs ? '?' + qs : ''}`;
            const response = await fetch(url, { credentials: 'include' });
            if (response.status === 401) {
                window.location.href = '/login';
                return;
            }
            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || `HTTP ${response.status}`);
            }
            const data = await response.json();
            setLogs(data.logs || []);
            setTotal(data.total || 0);
        } catch (err) {
            console.error('[AuditLogPage] fetch error', err);
            setError(err.message || 'Failed to load audit logs.');
        } finally {
            setLoading(false);
        }
    };

    // Debounced re-fetch when filters change
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setOffset(0);
            fetchLogs(0);
        }, 300);
        return () => clearTimeout(debounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    // Re-fetch when offset changes (pagination)
    useEffect(() => {
        fetchLogs(offset);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleClearFilters = () => {
        setFilters({ action: '', resource_type: '', start_date: '', end_date: '' });
    };

    const handleExport = async (format) => {
        try {
            const qs = buildQueryString({ format });
            const url = `${API_BASE_URL}/admin/audit-logs/export${qs ? '?' + qs : ''}`;
            const response = await fetch(url, { credentials: 'include' });
            if (!response.ok) {
                throw new Error(`Export failed: HTTP ${response.status}`);
            }
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = format === 'csv' ? 'audit_logs.csv' : 'audit_logs.json';
            a.click();
            URL.revokeObjectURL(objectUrl);
        } catch (err) {
            console.error('[AuditLogPage] export error', err);
            setError(err.message || 'Export failed.');
        }
    };

    const hasPrev = offset > 0;
    const hasNext = offset + limit < total;

    return (
        <div className="max-w-7xl mx-auto px-4 py-6">
            {/* Page Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Audit Log</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        System action history — CREATOR view only
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => handleExport('csv')}
                        className="px-4 py-2 text-sm font-medium bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors"
                    >
                        Export CSV
                    </button>
                    <button
                        onClick={() => handleExport('json')}
                        className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                    >
                        Export JSON
                    </button>
                </div>
            </div>

            {/* Error Banner */}
            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-300 rounded-md text-red-700 text-sm">
                    {error}
                </div>
            )}

            {/* Filter Controls */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                            Action
                        </label>
                        <input
                            type="text"
                            name="action"
                            value={filters.action}
                            onChange={handleFilterChange}
                            placeholder="e.g. CREATE, UPDATE, DELETE"
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                            Resource Type
                        </label>
                        <input
                            type="text"
                            name="resource_type"
                            value={filters.resource_type}
                            onChange={handleFilterChange}
                            placeholder="e.g. case, lead, user"
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                            Start Date
                        </label>
                        <input
                            type="date"
                            name="start_date"
                            value={filters.start_date}
                            onChange={handleFilterChange}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                            End Date
                        </label>
                        <input
                            type="date"
                            name="end_date"
                            value={filters.end_date}
                            onChange={handleFilterChange}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>
                <div className="mt-3 flex justify-end">
                    <button
                        onClick={handleClearFilters}
                        className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        Clear Filters
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        <span className="ml-3 text-gray-500 dark:text-gray-400 text-sm">Loading...</span>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="px-4 py-3 font-medium">Timestamp</th>
                                    <th className="px-4 py-3 font-medium">User</th>
                                    <th className="px-4 py-3 font-medium">Action</th>
                                    <th className="px-4 py-3 font-medium">Resource Type</th>
                                    <th className="px-4 py-3 font-medium">Resource ID</th>
                                    <th className="px-4 py-3 font-medium">IP Address</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {logs.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-8 text-center text-gray-400 dark:text-gray-500">
                                            No results found.
                                        </td>
                                    </tr>
                                ) : (
                                    logs.map((log) => (
                                        <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                            <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                                {log.created_at
                                                    ? new Date(log.created_at).toLocaleString()
                                                    : '—'}
                                            </td>
                                            <td className="px-4 py-3 text-gray-900 dark:text-white">
                                                <div className="font-medium">{log.user_email}</div>
                                                {log.user_name && log.user_name !== 'unknown' && (
                                                    <div className="text-xs text-gray-400">{log.user_name}</div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="px-2 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300">
                                                    {log.action}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                                                {log.resource_type || '—'}
                                            </td>
                                            <td className="px-4 py-3 text-gray-500 dark:text-gray-400 font-mono text-xs">
                                                {log.resource_id || '—'}
                                            </td>
                                            <td className="px-4 py-3 text-gray-500 dark:text-gray-400 font-mono text-xs">
                                                {log.ip_address || '—'}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {!loading && total > 0 && (
                <div className="flex items-center justify-between mt-4 text-sm text-gray-600 dark:text-gray-400">
                    <span>
                        Showing {offset + 1}–{Math.min(offset + limit, total)} of {total}
                    </span>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setOffset((prev) => Math.max(0, prev - limit))}
                            disabled={!hasPrev}
                            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setOffset((prev) => prev + limit)}
                            disabled={!hasNext}
                            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AuditLogPage;
